// display labels for ops response manager enum values. the api values are
// frozen (they key ddb rows and gsi partitions), so renames happen here.
// 'incident' reads as 'Issue' per anderson feedback -- the word was a
// trigger for drivers seeing the public form. category names are per tenant
// (FI-1944): categoryLabel() reads the tenant's label map and falls back to
// the slug.

export const TYPE_LABELS = {
  incident: 'Issue',
}

// mechanical fallback shared with the legacy readableLabel implementations:
// snake_case -> Title Case
function readable(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase())
}

export function typeLabel(value) {
  return TYPE_LABELS[value] || readable(value)
}

// ---- category names (FI-1944, #1816) ------------------------------------
// a fleet's own categories are keyed custom_<slug of the name> and carry
// their name in the categories row's custom_categories [{key, label,
// entry_type}]; a renamed built-in carries its name in label_overrides
// {slug: label}. the key never changes, so everything keyed by slug (routes,
// counts, archiving) is untouched by a rename.

export const CUSTOM_CATEGORY_PREFIX = 'custom_'

export const isCustomCategory = slug =>
  typeof slug === 'string' && slug.indexOf(CUSTOM_CATEGORY_PREFIX) === 0

// slug -> name from the payload's custom_categories and label_overrides.
// malformed definitions and blank names are skipped, so a caller can hand
// in whatever the api sent (or nothing)
export function customCategoryLabels(defs, overrides) {
  const out = {}
  if (overrides && typeof overrides === 'object' && !Array.isArray(overrides)) {
    Object.keys(overrides).forEach(slug => {
      const label = String(overrides[slug] || '').trim()
      if (slug && label) out[slug] = label
    })
  }
  const list = Array.isArray(defs) ? defs : []
  list.forEach(d => {
    const key = d && typeof d.key === 'string' ? d.key : ''
    const label = d && d.label ? String(d.label).trim() : ''
    if (key && label) out[key] = label
  })
  return out
}

// the tenant's name for a category: its own label when the map has one,
// else the slug made readable, minus the custom_ prefix (a custom category
// the map has not caught up with reads 'Flat Tire', never 'Custom Flat
// Tire'). '' for an empty slug
export function categoryLabel(slug, labels) {
  if (!slug) return ''
  const own = labels && Object.prototype.hasOwnProperty.call(labels, slug) ? labels[slug] : ''
  if (own) return String(own)
  const bare = isCustomCategory(slug) ? slug.slice(CUSTOM_CATEGORY_PREFIX.length) : slug
  return readable(bare)
}

export function entryTypeIcon(t) {
  if (t === 'incident') return 'fa-exclamation-circle'
  if (t === 'kudos') return 'fa-star'
  if (t === 'note') return 'fa-sticky-note-o'
  return 'fa-circle-o'
}

// ---- the event workflow ----------------------------------------------
// mirrors the FI-1815 backend _TRANSITIONS table (ops_desk/event_manager.py).
// shared by the Event Log's "Move to" buttons and the ERM Board's drag rules:
// a board that accepted a drop the server rejects would lie about the
// workflow, so both surfaces read the same table.

export const STATUS_NEW = 'new'
export const STATUS_IN_PROGRESS = 'in_progress'
export const STATUS_AWAITING_FOLLOWUP = 'awaiting_followup'
export const STATUS_RESOLVED = 'resolved'

export const STATUSES = [STATUS_NEW, STATUS_IN_PROGRESS, STATUS_AWAITING_FOLLOWUP, STATUS_RESOLVED]
export const OPEN_STATUSES = [STATUS_NEW, STATUS_IN_PROGRESS, STATUS_AWAITING_FOLLOWUP]

export const TRANSITIONS = {
  new: ['in_progress', 'awaiting_followup', 'resolved'],
  in_progress: ['awaiting_followup', 'resolved'],
  awaiting_followup: ['in_progress', 'resolved'],
  // reopen path. admin-only -- see canTransition below.
  resolved: ['in_progress'],
}

export const STATUS_LABELS = {
  new: 'New',
  in_progress: 'In progress',
  awaiting_followup: 'Awaiting follow-up',
  resolved: 'Resolved',
}

// terse labels for the inline list chip so a status never wraps the row to
// double height (the column is fixed-width and the chip is uppercased).
// the detail view + filters keep the full STATUS_LABELS; the list cell
// carries the full label on a hover title.
export const STATUS_SHORT_LABELS = {
  new: 'New',
  in_progress: 'In progress',
  awaiting_followup: 'Follow-up',
  resolved: 'Resolved',
}

export const STATUS_ICONS = {
  new: 'fa-circle-o',
  // play-circle reads as "underway"; fa-spinner implies the page is busy.
  in_progress: 'fa-play-circle-o',
  awaiting_followup: 'fa-clock-o',
  resolved: 'fa-check-circle',
}

export const STATUS_ACTION_LABELS = {
  new: 'Mark new',
  in_progress: 'Start',
  awaiting_followup: 'Awaiting follow-up',
  resolved: 'Resolve',
}

export const statusLabel = s => STATUS_LABELS[s] || readable(s)
export const statusShortLabel = s => STATUS_SHORT_LABELS[s] || statusLabel(s)
export const statusIcon = s => STATUS_ICONS[s] || 'fa-circle-o'
export const statusActionLabel = s => STATUS_ACTION_LABELS[s] || statusLabel(s)

// can `from` legally become `to` for this viewer? reopening a resolved event
// is admin-only server-side (transition_status raises Unauthorized otherwise),
// so the ui gates it the same way rather than offering a move that 401s.
export function canTransition(from, to, isAdmin) {
  if (!from || from === to) return false
  if (!(TRANSITIONS[from] || []).includes(to)) return false
  if (from === STATUS_RESOLVED && !isAdmin) return false
  return true
}

// ---- severity (FI-1933) ----------------------------------------------
// absent severity renders as normal: the field postdates the first events.

export const severityLabel = value => ({ high: 'High', normal: 'Normal', low: 'Low' }[value] || 'Normal')
export const severityKey = value => ((value === 'high' || value === 'low') ? value : 'normal')
// normal is the quiet default, so it earns no chip -- one on every row would
// be noise rather than signal.
export const showSeverityChip = value => value === 'high' || value === 'low'
